import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ExplainDeeperSheet from "@/components/ExplainDeeperSheet";
import MasteryBar from "@/components/MasteryBar";
import ModuleModesModal from "@/components/ModuleModesModal";
import BossQuizModal from "@/components/BossQuizModal";
import CourseReviewSection from "@/components/CourseReviewSection";
import CommunityForumPanel from "@/components/CommunityForumPanel";
import AffiliateLibrarian from "@/components/AffiliateLibrarian";
import BentoJsonLd from "@/components/bento/BentoJsonLd";
import BentoStepCard from "@/components/bento/BentoStepCard";
import BossQuizUnlockCard from "@/components/bento/BossQuizUnlockCard";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bookmark, CalendarPlus, Trophy } from "lucide-react";

/**
 * BentoPath — Iter84 refactor.
 *
 * Orchestrator for the path-view bento. Holds shared state (progress,
 * generating spinner, mastery refresh key, boss-quiz status) and wires
 * it to extracted presentational sub-components under `components/bento/`.
 *
 * Sub-components:
 *   - BentoJsonLd          → Schema.org JSON-LD
 *   - BentoStepCard        → individual step card (rendered 5x)
 *   - BossQuizUnlockCard   → 3-state mastery exam unlock card
 *
 * Bottom of file still owns: AffiliateLibrarian, CourseReviewSection,
 * CommunityForumPanel and the modal trio (ExplainDeeper, ModuleModes,
 * BossQuiz) because each is mounted exactly once and is small enough
 * inline.
 */

const ACCENTS = [
  { border: "border-[#00FF9D]/25", badge: "bg-[#00FF9D]/10 text-[#00FF9D]", dot: "bg-[#00FF9D]" },
  { border: "border-[#0044FF]/30", badge: "bg-[#0044FF]/15 text-[#8aa8ff]", dot: "bg-[#0044FF]" },
  { border: "border-[#FF7B00]/25", badge: "bg-[#FF7B00]/10 text-[#ffb067]", dot: "bg-[#FF7B00]" },
  { border: "border-[#5D3FD3]/30", badge: "bg-[#5D3FD3]/15 text-[#b9a8ff]", dot: "bg-[#5D3FD3]" },
  { border: "border-white/10", badge: "bg-white/5 text-white/70", dot: "bg-white/70" },
];

export default function BentoPath({ path, onCreditsChange, onNeedCredit, onLibraryChange }) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(null);
  const [generating, setGenerating] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [masteryKey, setMasteryKey] = useState(0);
  const [modeOpen, setModeOpen] = useState(null); // null | "analogy" | "simulate"
  const [modeStep, setModeStep] = useState(null);
  const [resumeStep, setResumeStep] = useState(null); // B3 — last step the user opened
  const [completedSteps, setCompletedSteps] = useState([]); // pivot — finished step indices
  const [bossOpen, setBossOpen] = useState(false);
  const [bossStatus, setBossStatus] = useState(null);
  const navigate = useNavigate();

  // B3 — Hydrate progress on mount so we can surface a "Resume" CTA.
  useEffect(() => {
    if (!path?.path_id) return;
    api.get(`/progress/${path.path_id}`)
      .then(({ data }) => {
        if (data?.exists && Number.isInteger(data.last_step_index)) {
          setResumeStep(data.last_step_index);
        }
      })
      .catch(() => {});
    api.get(`/path/${path.path_id}/completion`)
      .then(({ data }) => setCompletedSteps(Array.isArray(data?.completed_steps) ? data.completed_steps : []))
      .catch(() => {});
    api.get("/boss-quiz/status", { params: { path_id: path.path_id } })
      .then(({ data }) => setBossStatus(data))
      .catch(() => {});
  }, [path?.path_id, masteryKey]);

  // B3 — Persist whichever step the user is reading. Fire-and-forget.
  const persistProgress = (stepIdx, part = 1) => {
    if (!path?.path_id) return;
    api.post("/progress", {
      path_id: path.path_id,
      last_step_index: stepIdx,
      last_part: part,
      last_difficulty_tier: path.difficulty_tier || null,
    }).catch(() => {});
  };

  const explain = (i) => { setActiveStep(i); setSheetOpen(true); persistProgress(i, 1); };
  const openMode = (i, mode) => { setModeStep(i); setModeOpen(mode); persistProgress(i, 1); };

  const syncPlanner = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/planner/sync-from-path", { path_id: path.path_id, days_spacing: 2 });
      toast.success(`Synced ${data.created} tasks to Planner`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const startExam = async (i) => {
    setGenerating(i);
    try {
      const step = path.steps[i];
      const fd = new FormData();
      fd.append("topic", `${path.goal} — ${step.title}`);
      fd.append("path_id", path.path_id);
      fd.append("content", step.detail || step.objective || "");
      const { data } = await api.post("/exam/generate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onCreditsChange?.(data.credits_remaining);
      sessionStorage.setItem(`exam:${data.exam_id}`, JSON.stringify(data));
      toast.success("Exam unlocked");
      navigate(`/exam/${data.exam_id}`);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        onNeedCredit?.();
      } else {
        toast.error(e?.response?.data?.detail || "Exam generation failed");
      }
    } finally {
      setGenerating(null);
    }
  };

  if (!path) return null;
  return (
    <div className="space-y-6 px-4 sm:px-0" data-testid="bento-path">
      <BentoJsonLd path={path} />

      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow mb-2">Learning Path</div>
          <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight max-w-2xl">
            {path.goal}
          </h2>
          {path.summary && <p className="text-white/55 mt-2 max-w-xl text-sm">{path.summary}</p>}

          {/* AI Citation Hook — 40-word machine-readable summary visible
              to humans + crawled by AI engines. Same selector as SSR. */}
          {path.ai_summary && (
            <section
              className="mt-4 max-w-2xl rounded-xl border border-[#00FF9D]/22 bg-gradient-to-br from-[#0044FF]/[0.07] to-[#00FF9D]/[0.04] px-4 py-3"
              data-testid="ai-citation-hook"
              itemScope
              itemType="https://schema.org/AbstractObject"
            >
              <div className="text-[10px] font-display tracking-[0.18em] uppercase text-[#00FF9D]/85 mb-1.5">
                AI Citation Summary · Machine-Readable
              </div>
              <p className="text-[13.5px] leading-relaxed text-white/80" itemProp="abstract">
                {path.ai_summary}
              </p>
              {Array.isArray(path.key_facts) && path.key_facts.length > 0 && (
                <ul className="mt-2 ml-4 text-[12.5px] text-white/60 list-disc space-y-0.5" data-testid="ai-citation-facts">
                  {path.key_facts.slice(0, 5).map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {Number.isInteger(resumeStep) && path.steps?.[resumeStep] && (
            <button
              type="button"
              onClick={() => explain(resumeStep)}
              className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#00FF9D]/40 bg-[#00FF9D]/[0.08] hover:bg-[#00FF9D]/[0.16] px-3 py-1.5 text-xs text-[#00FF9D] font-display font-semibold transition"
              data-testid="bento-resume-cta"
            >
              <Bookmark size={12} /> Resume · Step {resumeStep + 1}
              <span className="text-[10px] text-[#00FF9D]/70 font-normal">
                {path.steps[resumeStep].title}
              </span>
            </button>
          )}
          {path.difficulty_tier && (
            <span
              className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#5D3FD3]/15 border border-[#5D3FD3]/30 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#b9a8ff] font-mono-oak"
              data-testid="bento-tier-badge"
            >
              {path.difficulty_tier}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={syncPlanner}
            disabled={syncing}
            className="text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#0044FF]/40 bg-[#0044FF]/10 text-[#8aa8ff] hover:bg-[#0044FF]/20 transition disabled:opacity-50"
            data-testid="btn-sync-planner"
          >
            <CalendarPlus size={13} /> {syncing ? "Syncing…" : "Sync to Planner"}
          </button>
          <button
            onClick={() => navigate(`/certificate/${path.path_id}`)}
            className="text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#ffb067]/40 bg-[#ffb067]/10 text-[#ffb067] hover:bg-[#ffb067]/20 transition"
            data-testid="btn-view-certificate"
          >
            <Trophy size={13} /> Certificate
          </button>
        </div>
      </div>

      <MasteryBar pathId={path.path_id} refreshKey={masteryKey} />

      {bossStatus && (
        <BossQuizUnlockCard
          status={bossStatus}
          pathGoal={path.goal}
          onOpen={() => setBossOpen(true)}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-6 gap-5">
        {path.steps.map((s, i) => {
          // bento layout: #1 spans 3, #2 spans 3, #3-#5 span 2.
          const span = i === 0 || i === 1 ? "md:col-span-3" : "md:col-span-2";
          return (
            <BentoStepCard
              key={i}
              step={s}
              index={i}
              accent={ACCENTS[i % ACCENTS.length]}
              span={span}
              isCompleted={completedSteps.includes(i)}
              isLastStep={i === path.steps.length - 1}
              generating={generating}
              pathId={path.path_id}
              onExplain={explain}
              onMode={openMode}
              onExam={startExam}
              onNextStep={(nextIdx) => persistProgress(nextIdx, 1)}
            />
          );
        })}
      </div>

      {/* Contextual books, course reviews, community forum — bottom rail */}
      <AffiliateLibrarian
        goal={path.goal}
        keywords={(path.steps || []).flatMap((s) => Array.isArray(s.key_terms) ? s.key_terms : []).slice(0, 8)}
      />
      <CourseReviewSection pathId={path.path_id} pathGoal={path.goal} />
      <div className="mt-6">
        <CommunityForumPanel
          pathId={path.path_id}
          pathGoal={path.goal}
        />
      </div>

      <ExplainDeeperSheet
        open={sheetOpen}
        onOpenChange={(v) => { setSheetOpen(v); if (!v) setMasteryKey((k) => k + 1); }}
        path={path}
        stepIndex={activeStep}
        onCreditsNeeded={onNeedCredit}
        onLibraryChange={() => { onLibraryChange?.(); setMasteryKey((k) => k + 1); }}
        onNextStep={
          activeStep != null && activeStep < (path?.steps?.length || 0) - 1
            ? () => {
                // Close-and-reopen on the next step so the part cache
                // re-hydrates from scratch (avoids jumpy in-place swap).
                const next = activeStep + 1;
                setSheetOpen(false);
                setTimeout(() => {
                  setActiveStep(next);
                  setSheetOpen(true);
                  persistProgress(next, 1);
                  setMasteryKey((k) => k + 1);
                }, 180);
              }
            : null
        }
      />
      <ModuleModesModal
        open={modeOpen !== null}
        onOpenChange={(v) => { if (!v) setModeOpen(null); }}
        path={path}
        stepIndex={modeStep}
        mode={modeOpen || "analogy"}
        onCreditsChange={(c) => onCreditsChange?.(c)}
      />
      <BossQuizModal
        open={bossOpen}
        onOpenChange={(v) => {
          setBossOpen(v);
          if (!v) {
            // Refresh status — they may have just minted a seal.
            api.get("/boss-quiz/status", { params: { path_id: path.path_id } })
              .then(({ data }) => setBossStatus(data))
              .catch(() => {});
          }
        }}
        pathId={path.path_id}
        pathGoal={path.goal}
      />
    </div>
  );
}
